// Customer Story detail — hero (Figma 12250:40015).
// Left-aligned header on a grey-50 surface: customer photo (or video tile) beside
// fact tags, headline and summary, then a row of stat cards and a blue
// testimonial band. Every piece but the title is optional — studies without a
// photo, stats or quote collapse to what they have.

import Image from "next/image";

import { Link } from "@/components/ui/link";
import { cnSectionHeroBelowNav } from "@/lib/nav-layout";
import { cn } from "@/lib/utils";

import { CustomerStorySidebarVideo } from "./sidebar-video";
import { CustomerStoryStatValue } from "./stat-value";

export interface CustomerStoryDetailHeroTag {
  label: string;
  value: string;
  href?: string;
}

interface CustomerStoryDetailHeroProps {
  title: string;
  /** Summary under the headline. */
  description?: string;
  /** Industry / region / website chips above the headline. */
  tags?: CustomerStoryDetailHeroTag[];
  image?: { src: string; alt: string };
  video?: {
    playbackId: string;
    playLabel?: string;
    thumbnailSrc?: string;
    thumbnailAlt?: string;
  };
  /** Up to three headline numbers; extras are ignored. */
  stats?: { value: string; label: string }[];
  quote?: { text: string; highlight?: string; author: string; role: string };
}

const MAX_STATS = 3;
// Equal-width columns for however many stats the study carries.
const STAT_COLUMNS: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
};

export function CustomerStoryDetailHero({
  title,
  description,
  tags = [],
  image,
  video,
  stats = [],
  quote,
}: CustomerStoryDetailHeroProps) {
  const heroStats = stats.slice(0, MAX_STATS);
  const hasMedia = Boolean(video || image);
  const quoteText = quote
    ? [quote.text, quote.highlight].filter(Boolean).join(" ")
    : "";

  return (
    <section
      data-testid="customer-story-hero"
      className={cnSectionHeroBelowNav(
        "bg-grey-50 border-grey-100 border-b px-6 !pb-8 lg:px-20 lg:!pb-20"
      )}
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-6">
        <div
          className={cn(
            "flex flex-col gap-6 lg:items-center lg:gap-12",
            hasMedia ? "lg:flex-row" : "lg:max-w-[608px]"
          )}
        >
          {video ? (
            <div className="min-w-0 lg:flex-1">
              <CustomerStorySidebarVideo
                playbackId={video.playbackId}
                title={title}
                playLabel={video.playLabel}
                thumbnailSrc={video.thumbnailSrc ?? image?.src}
                thumbnailAlt={video.thumbnailAlt ?? image?.alt}
                sizes="(min-width: 1024px) 680px, 100vw"
                priority
                className="aspect-[680/382] rounded-2xl"
              />
            </div>
          ) : image ? (
            <div className="relative aspect-[680/382] w-full min-w-0 overflow-hidden rounded-2xl lg:flex-1">
              <Image
                src={image.src}
                alt={image.alt}
                fill
                priority
                sizes="(min-width: 1024px) 680px, 100vw"
                className="object-cover"
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-4 lg:w-[608px] lg:shrink-0">
            {tags.length > 0 ? (
              <ul className="flex flex-wrap gap-3">
                {tags.map((tag) => (
                  <li
                    key={tag.label}
                    className="bg-grey-100 text-grey-950 text-ds-body-xs rounded px-2 py-1"
                  >
                    {tag.href ? (
                      <Link
                        href={tag.href}
                        external
                        className="hover:opacity-70"
                      >
                        {tag.label}: {tag.value}
                      </Link>
                    ) : (
                      `${tag.label}: ${tag.value}`
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            <h1 className="text-ds-heading-h3 lg:text-ds-heading-h2 text-grey-950">
              {title}
            </h1>
            {description ? (
              <p className="text-ds-body-md text-grey-500 leading-[1.5]">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        {heroStats.length > 0 ? (
          <dl
            className={cn(
              "grid grid-cols-1 gap-3 lg:gap-6",
              STAT_COLUMNS[heroStats.length]
            )}
          >
            {heroStats.map((stat) => (
              <div
                key={stat.label}
                className="customer-story-stat-card bg-grey-0 flex flex-col gap-9 rounded-xl p-6"
              >
                <dt className="text-ds-body-sm text-grey-950 leading-[1.5]">
                  {stat.label}
                </dt>
                <dd>
                  <CustomerStoryStatValue
                    value={stat.value}
                    hoverGradient
                    className="text-ds-heading-h4 lg:text-ds-heading-h1"
                  />
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {quote ? (
          <figure className="from-pulse-700 to-pulse-900 flex flex-col gap-6 rounded-2xl bg-gradient-to-br from-40% p-6 lg:p-12">
            <blockquote className="text-ds-heading-h5 lg:text-ds-heading-h3 text-grey-0 leading-[1.4] lg:max-w-[969px] lg:leading-[1.4] lg:tracking-[-0.02em]">
              {`"${quoteText}"`}
            </blockquote>
            <figcaption className="text-ds-body-md lg:text-ds-heading-h5 text-pulse-300 leading-[1.4] lg:leading-[1.4] lg:tracking-[-0.02em]">
              {quote.author}, {quote.role}
            </figcaption>
          </figure>
        ) : null}
      </div>
    </section>
  );
}
